import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel, SelectSeparator } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BarChart3, Users, Calendar, HandHelping, Rows3, UserCircle, Tag, TrendingUp, Hash, UserCheck, ArrowRight, CalendarClock, Eye, ChevronLeft, Cake, MessageCircle, Copy, Check } from 'lucide-react';
import { fmtDate, calcAge, getFilaTextColor } from '@/lib/helpers';
import { toast } from 'sonner';
import LinksAnalytics from './LinksAnalytics';
import { getEngagementDisplay } from '@/lib/engagement';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const API = `${window.location.origin}/api`;

function EventosModal({ open, onOpenChange, data, loading }) {
  const [filter, setFilter] = useState('presentes');
  const [subModal, setSubModal] = useState(null);
  const [subLoading, setSubLoading] = useState(false);

  // Reset filter when modal opens with new data
  useEffect(() => {
    if (open) { setFilter('presentes'); setSubModal(null); }
  }, [open, data?.pessoa_nome]);

  const openPresentes = async (eventId) => {
    setSubLoading(true);
    setSubModal(null);
    try {
      const r = await fetch(`${API}/reports/evento/${eventId}/presentes`, { credentials: 'include' });
      if (r.ok) setSubModal(await r.json());
    } catch (err) { console.error(err); }
    finally { setSubLoading(false); }
  };

  const filtered = data ? data.eventos.filter(ev => filter === 'presentes' ? ev.presente : !ev.presente) : [];
  const totalPresentes = data ? data.eventos.filter(ev => ev.presente).length : 0;
  const totalAusentes = data ? data.eventos.filter(ev => !ev.presente).length : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col" data-testid="modal-pessoa-eventos">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-slate-900">
            {subModal ? (
              <div className="flex items-center gap-2">
                <button onClick={() => setSubModal(null)} className="p-1 rounded hover:bg-slate-100 transition-colors" data-testid="btn-voltar-eventos">
                  <ChevronLeft className="w-5 h-5 text-slate-500" />
                </button>
                <span>Presentes em {subModal.evento_nome}</span>
              </div>
            ) : data ? `Eventos de ${data.pessoa_nome}` : 'Carregando...'}
          </DialogTitle>
        </DialogHeader>

        {subLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-slate-500 mx-auto"></div>
            <p className="mt-3 text-sm text-slate-500">Carregando presentes...</p>
          </div>
        ) : subModal ? (
          <div className="flex-1 overflow-y-auto -mx-6 px-6">
            <p className="text-sm text-slate-500 mb-3">{subModal.total} {subModal.total === 1 ? 'pessoa presente' : 'pessoas presentes'}</p>
            {subModal.presentes.length > 0 ? (
              <div className="space-y-0">
                {subModal.presentes.map((p) => (
                  <div key={p.pessoa_id} className="flex items-center gap-3 px-3 py-2 border-b border-slate-100 last:border-b-0">
                    <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-xs flex-shrink-0">{p.nome_completo.charAt(0)}</div>
                    <p className="font-medium text-sm text-slate-900">{p.nome_completo}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Nenhuma presença registrada</p>
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto -mx-6 px-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-slate-500 mx-auto"></div>
                <p className="mt-3 text-sm text-slate-500">Carregando eventos...</p>
              </div>
            ) : data ? (
              <>
                <div className="flex gap-2 mb-3" data-testid="filter-eventos-presenca">
                  <button onClick={() => setFilter('presentes')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${filter === 'presentes' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`} data-testid="filter-presentes">
                    Presentes ({totalPresentes})
                  </button>
                  <button onClick={() => setFilter('ausentes')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${filter === 'ausentes' ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`} data-testid="filter-ausentes">
                    Ausentes ({totalAusentes})
                  </button>
                </div>
                {filtered.length > 0 ? (
                  <div className="space-y-0">
                    {filtered.map((ev) => (
                      <div key={ev.event_id} className="flex items-center gap-3 px-3 py-2.5 border-b border-slate-100 last:border-b-0">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${ev.presente ? 'bg-green-50' : 'bg-red-50'}`}>
                          <Calendar className={`w-3.5 h-3.5 ${ev.presente ? 'text-green-500' : 'text-red-400'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-slate-900 truncate">{ev.name}</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-slate-500">{fmtDate(ev.date)}</span>
                            {ev.tipo_evento && <Badge variant="outline" className="text-[10px] py-0">{ev.tipo_evento}</Badge>}
                          </div>
                        </div>
                        <button onClick={() => openPresentes(ev.event_id)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0" data-testid={`btn-ver-presentes-${ev.event_id}`} title="Ver presentes">
                          <Eye className="w-4 h-4 text-slate-400" />
                        </button>
                        <Badge className={`text-[10px] flex-shrink-0 ${ev.status === 'aberto' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>
                          {ev.status === 'aberto' ? 'Aberto' : 'Fechado'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">{filter === 'presentes' ? 'Nenhum evento com presença' : 'Nenhuma ausência registrada'}</p>
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const ENGAJAMENTO_REPORTS = [
  { key: 'pessoas', label: 'Pessoas', icon: Users, color: 'bg-blue-100 text-blue-600' },
  { key: 'eventos', label: 'Eventos', icon: Calendar, color: 'bg-purple-100 text-purple-600' },
];

const OUTROS_REPORTS = [
  { key: 'filas_recitativos', label: 'Filas de Recitativos', icon: Rows3, color: 'bg-amber-100 text-amber-600' },
  { key: 'aniversariantes', label: 'Aniversariantes', icon: Cake, color: 'bg-pink-100 text-pink-600' },
  { key: 'genero', label: 'Gênero', icon: UserCircle, color: 'bg-rose-100 text-rose-600' },
  { key: 'nao_batizados', label: 'Não Batizados', icon: UserCheck, color: 'bg-red-100 text-red-600' },
];

const MIGRACOES_REPORTS = [
  { key: 'migracoes_historico', label: 'Histórico Migrações', icon: ArrowRight, color: 'bg-teal-100 text-teal-600' },
  { key: 'migracoes_futuras', label: 'Migrações Futuras', icon: CalendarClock, color: 'bg-emerald-100 text-emerald-600' },
];

const ANALYTICS_REPORTS = [
  { key: 'links_analytics', label: 'Links', icon: Eye, color: 'bg-cyan-100 text-cyan-600' },
];

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const MENSAGENS_ANIV = [
  (nome) => `A paz de Deus, tudo bem? Feliz aniversário, ${nome}! Que este novo ano da sua vida seja cheio de saúde, paz e alegria! Deus abençoe.`,
  (nome) => `A paz de Deus, tudo bem? Parabéns, ${nome}! Que o Senhor continue guiando seus passos e enchendo sua vida de bênçãos! Deus abençoe.`,
  (nome) => `A paz de Deus, tudo bem? Feliz aniversário, ${nome}! Que este dia seja repleto de amor, alegria e a presença de Deus! Deus abençoe.`,
  (nome) => `A paz de Deus, tudo bem? Parabéns pelo seu dia, ${nome}! Que Deus renove suas forças e multiplique suas bênçãos neste novo ciclo! Deus abençoe.`,
  (nome) => `A paz de Deus, tudo bem? Feliz aniversário, ${nome}! Celebramos a vida que Deus te deu! Muita paz e prosperidade! Deus abençoe.`,
];

function enviarWhatsAppAniv(nome, whatsapp) {
  const numero = whatsapp.replace(/\D/g, '');
  const msg = MENSAGENS_ANIV[Math.floor(Math.random() * MENSAGENS_ANIV.length)](nome);
  const url = `https://wa.me/55${numero}?text=${encodeURIComponent(msg)}`;
  const a = document.createElement('a'); a.href = url; a.target = '_blank'; a.rel = 'noopener noreferrer';
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  navigator.clipboard.writeText(url).then(() => { toast.success('Link copiado! Se não abriu, cole numa nova aba.', { duration: 5000 }); }).catch(() => {});
}

function calcAgeAniv(dobStr) {
  if (!dobStr) return null;
  const [y, m, d] = dobStr.split('-').map(Number);
  const today = new Date();
  let age = today.getFullYear() - y;
  if (today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)) age--;
  return age;
}

function getFilaNomeAniv(pessoa, filas) {
  const filaId = pessoa.fila_id;
  if (filaId) { const fila = (filas || []).find(f => f.fila_id === filaId); if (fila) return fila.nome; }
  const age = calcAgeAniv(pessoa.data_nascimento);
  if (age === null) return null;
  for (const f of (filas || [])) {
    const ini = f.idade_inicial; const fim = f.idade_final;
    if (fim !== null && fim !== undefined) { if (age >= ini && age <= fim) return f.nome; }
    else { if (age >= ini) return f.nome; }
  }
  return null;
}

function AniversariantesReport({ pessoas }) {
  const [mesSelecionado, setMesSelecionado] = useState(new Date().getMonth());

  const contagemPorMes = Array.from({ length: 12 }, (_, i) =>
    (pessoas || []).filter(p => p.data_nascimento && parseInt(p.data_nascimento.split('-')[1], 10) - 1 === i).length
  );

  const aniversariantes = (pessoas || [])
    .filter(p => p.data_nascimento && parseInt(p.data_nascimento.split('-')[1], 10) - 1 === mesSelecionado)
    .map(p => {
      const parts = p.data_nascimento.split('-');
      return { ...p, dia: parseInt(parts[2], 10), idade: calcAgeAniv(p.data_nascimento) };
    })
    .sort((a, b) => a.dia - b.dia);

  return (
    <Card className="bg-white border-0 shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-pink-100 flex items-center justify-center flex-shrink-0">
            <Cake className="w-5 h-5 text-pink-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Aniversariantes</h2>
            <p className="text-sm text-slate-500">Selecione o mês</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3" data-testid="meses-menu-report">
          {MESES.map((mes, i) => (
            <button key={i} onClick={() => setMesSelecionado(i)} data-testid={`mes-report-${i}`}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${mesSelecionado === i ? 'bg-pink-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {mes.slice(0, 3)}
              {contagemPorMes[i] > 0 && <span className="ml-1 opacity-75">({contagemPorMes[i]})</span>}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-semibold text-slate-700">{MESES[mesSelecionado]}</h3>
          <Badge className="bg-pink-100 text-pink-700 text-xs font-semibold">{aniversariantes.length}</Badge>
        </div>
        <div className="space-y-0" data-testid="aniversariantes-report-list">
          {aniversariantes.map((p) => (
            <div key={p.pessoa_id} className="px-3 py-2.5 border-b border-slate-100 last:border-b-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                  {p.dia}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-sm text-slate-900 truncate">{p.nome_completo}</p>
                    {p.idade !== null && <Badge variant="outline" className="text-xs">{p.idade} anos</Badge>}
                    {p.genero && <Badge variant="outline" className="text-xs">{p.genero}</Badge>}
                    <span className="flex items-center gap-1 text-xs text-slate-500">
                      <Calendar className="w-3 h-3" />{String(p.dia).padStart(2, '0')}/{String(mesSelecionado + 1).padStart(2, '0')}
                    </span>
                  </div>
                </div>
                {p.whatsapp && (
                  <button onClick={() => enviarWhatsAppAniv(p.nome_completo, p.whatsapp)} data-testid={`whatsapp-report-${p.pessoa_id}`}
                    className="w-7 h-7 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center transition-colors flex-shrink-0" title="Enviar WhatsApp">
                    <MessageCircle className="w-3.5 h-3.5 text-white" />
                  </button>
                )}
              </div>
            </div>
          ))}
          {aniversariantes.length === 0 && (
            <div className="text-center py-8 text-slate-400">
              <Cake className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Nenhum aniversariante em {MESES[mesSelecionado]}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function RelatoriosTab({ frequencyReport, auxiliarReport, eventsEngagement, pessoas, tiposEvento = [], onEditPessoa, showEventosBtn = false, engajamentoDisplay = 'niveis', isAuxiliar = false }) {
  const [active, setActive] = useState('pessoas');
  const [tipoEventoReport, setTipoEventoReport] = useState(null);
  const [tipoLoading, setTipoLoading] = useState(false);

  const handleSelectTipoEvento = async (tipoId) => {
    setActive(`tipo_${tipoId}`);
    setTipoLoading(true);
    try {
      const r = await fetch(`${API}/reports/by-tipo-evento/${tipoId}`, { credentials: 'include' });
      if (r.ok) setTipoEventoReport(await r.json());
    } catch (err) { console.error(err); }
    finally { setTipoLoading(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Sidebar */}
      <div className="lg:col-span-1">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center flex-shrink-0">
                <BarChart3 className="w-5 h-5 text-cyan-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Relatórios</h2>
                  <Badge className="bg-cyan-100 text-cyan-700 text-sm font-bold">{ENGAJAMENTO_REPORTS.length + OUTROS_REPORTS.length + MIGRACOES_REPORTS.length + tiposEvento.length}</Badge>
                </div>
                <p className="text-sm text-slate-500">Selecione um tipo</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Engajamento</p>
            <div className="space-y-2" data-testid="reports-menu">
              {ENGAJAMENTO_REPORTS.map((r) => {
                const Icon = r.icon;
                return (
                  <button key={r.key} onClick={() => setActive(r.key)} data-testid={`report-menu-${r.key}`}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 flex items-center gap-3 ${active === r.key ? 'border-cyan-600 bg-cyan-50 shadow-sm' : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${r.color.split(' ')[0]}`}>
                      <Icon className={`w-4 h-4 ${r.color.split(' ')[1]}`} />
                    </div>
                    <p className={`font-semibold text-sm ${active === r.key ? 'text-cyan-700' : 'text-slate-900'}`}>{r.label}</p>
                  </button>
                );
              })}
            </div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 mt-5">Outros</p>
            <div className="space-y-2">
              {OUTROS_REPORTS.map((r) => {
                const Icon = r.icon;
                return (
                  <button key={r.key} onClick={() => setActive(r.key)} data-testid={`report-menu-${r.key}`}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 flex items-center gap-3 ${active === r.key ? 'border-cyan-600 bg-cyan-50 shadow-sm' : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${r.color.split(' ')[0]}`}>
                      <Icon className={`w-4 h-4 ${r.color.split(' ')[1]}`} />
                    </div>
                    <p className={`font-semibold text-sm ${active === r.key ? 'text-cyan-700' : 'text-slate-900'}`}>{r.label}</p>
                  </button>
                );
              })}
            </div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 mt-5">Migrações</p>
            <div className="space-y-2" data-testid="reports-migracoes-menu">
              {MIGRACOES_REPORTS.map((r) => {
                const Icon = r.icon;
                return (
                  <button key={r.key} onClick={() => setActive(r.key)} data-testid={`report-menu-${r.key}`}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 flex items-center gap-3 ${active === r.key ? 'border-cyan-600 bg-cyan-50 shadow-sm' : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${r.color.split(' ')[0]}`}>
                      <Icon className={`w-4 h-4 ${r.color.split(' ')[1]}`} />
                    </div>
                    <p className={`font-semibold text-sm ${active === r.key ? 'text-cyan-700' : 'text-slate-900'}`}>{r.label}</p>
                  </button>
                );
              })}
            </div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 mt-5">Analytics</p>
            <div className="space-y-2" data-testid="reports-analytics-menu">
              {ANALYTICS_REPORTS.map((r) => {
                const Icon = r.icon;
                return (
                  <button key={r.key} onClick={() => setActive(r.key)} data-testid={`report-menu-${r.key}`}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 flex items-center gap-3 ${active === r.key ? 'border-cyan-600 bg-cyan-50 shadow-sm' : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${r.color.split(' ')[0]}`}>
                      <Icon className={`w-4 h-4 ${r.color.split(' ')[1]}`} />
                    </div>
                    <p className={`font-semibold text-sm ${active === r.key ? 'text-cyan-700' : 'text-slate-900'}`}>{r.label}</p>
                  </button>
                );
              })}
            </div>
            {tiposEvento.length > 0 && (
              <>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 mt-5">Tipos de Evento</p>
                <div className="space-y-2" data-testid="reports-tipos-evento-menu">
                  {tiposEvento.map((t) => {
                    const key = `tipo_${t.tipo_evento_id}`;
                    return (
                      <button key={key} onClick={() => handleSelectTipoEvento(t.tipo_evento_id)} data-testid={`report-menu-${key}`}
                        className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 flex items-center gap-3 ${active === key ? 'border-cyan-600 bg-cyan-50 shadow-sm' : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}>
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-indigo-100">
                          <Tag className="w-4 h-4 text-indigo-600" />
                        </div>
                        <p className={`font-semibold text-sm ${active === key ? 'text-cyan-700' : 'text-slate-900'}`}>{t.nome}</p>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Content */}
      <div className="lg:col-span-3">
        {active === 'pessoas' && <PessoasEngajadas data={frequencyReport} onEditPessoa={onEditPessoa} showEventosBtn={showEventosBtn} engajamentoDisplay={engajamentoDisplay} isAuxiliar={isAuxiliar} />}
        {active === 'eventos' && <EventosEngajados data={eventsEngagement} />}
        {active === 'filas_recitativos' && <FilasRecitativosReport />}
        {active === 'aniversariantes' && <AniversariantesReport pessoas={pessoas || []} />}
        {active === 'genero' && <RelatorioGenero pessoas={pessoas || []} onEditPessoa={onEditPessoa} engajamentoDisplay={engajamentoDisplay} isAuxiliar={isAuxiliar} />}
        {active === 'nao_batizados' && <RelatorioNaoBatizados pessoas={pessoas || []} onEditPessoa={onEditPessoa} />}
        {active === 'migracoes_historico' && <MigracoesHistorico onEditPessoa={onEditPessoa} />}
        {active === 'migracoes_futuras' && <MigracoesFuturas onEditPessoa={onEditPessoa} />}
        {active === 'links_analytics' && <LinksAnalytics />}
        {active.startsWith('tipo_') && (
          tipoLoading ? (
            <Card className="bg-white border-0 shadow-sm">
              <CardContent className="py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                  <p className="mt-3 text-sm text-slate-500">Carregando relatório...</p>
                </div>
              </CardContent>
            </Card>
          ) : tipoEventoReport ? (
            <RelatorioTipoEvento data={tipoEventoReport} />
          ) : (
            <Card className="bg-white border-0 shadow-sm">
              <CardContent className="py-12 text-center">
                <Tag className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Nenhum dado encontrado</p>
              </CardContent>
            </Card>
          )
        )}
      </div>
    </div>
  );
}


function FilasRecitativosReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [funcsR, pessoasR] = await Promise.all([
          fetch(`${API}/service-functions`, { credentials: 'include' }),
          fetch(`${API}/pessoas?status=aprovado`, { credentials: 'include' }),
        ]);
        const funcs = funcsR.ok ? await funcsR.json() : [];
        const pessoas = pessoasR.ok ? await pessoasR.json() : [];
        const recFuncs = funcs.filter(f => f.tipo === 'recitativo' && f.active && !f.deleted);

        // Build pessoa map
        const pessoaMap = {};
        for (const p of pessoas) pessoaMap[p.pessoa_id] = p;

        // Calculate ages
        const calcAge = (dob) => {
          if (!dob) return null;
          const birth = new Date(dob);
          const now = new Date();
          let age = now.getFullYear() - birth.getFullYear();
          if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) age--;
          return age;
        };

        // Group by fila (function)
        const groups = {};
        for (const func of recFuncs) {
          const elig = func.eligibility || {};
          const key = `${func.nome}_${elig.gender || ''}`;
          if (!groups[key]) {
            groups[key] = {
              nome: func.nome,
              genero: elig.gender || '',
              idade_min: elig.idade_min,
              idade_max: elig.idade_max,
              auxiliares: [],
              pessoas: [],
              order: func.order || 0,
            };
          }
          // Add assigned auxiliares
          for (const pid of (func.assigned_people || [])) {
            const p = pessoaMap[pid];
            if (p && !groups[key].auxiliares.some(a => a.pessoa_id === pid)) {
              groups[key].auxiliares.push({ pessoa_id: pid, nome: p.apelido || p.nome_completo?.split(' ')[0] || '' });
            }
          }
        }

        // Assign pessoas to groups by age and gender
        for (const p of pessoas) {
          if (p.deleted) continue;
          const age = calcAge(p.data_nascimento);
          if (age === null) continue;
          const genero = (p.genero || '').toLowerCase();
          for (const g of Object.values(groups)) {
            if (g.genero && g.genero !== genero) continue;
            const min = g.idade_min ?? 0;
            const max = g.idade_max;
            if (age >= min && (max === null || max === undefined || age <= max)) {
              if (!g.pessoas.some(x => x.pessoa_id === p.pessoa_id)) {
                g.pessoas.push({ pessoa_id: p.pessoa_id, nome: p.nome_completo, idade: age, batizado: !!p.batizado, whatsapp: p.whatsapp || '' });
              }
            }
          }
        }

        // Sort groups and pessoas
        const sorted = Object.values(groups).sort((a, b) => a.order - b.order);
        for (const g of sorted) {
          g.pessoas.sort((a, b) => a.nome.localeCompare(b.nome));
        }

        setData(sorted);
      } catch {} finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const copyGroup = (g) => {
    const ageLabel = g.idade_max != null ? `${g.idade_min || 0} à ${g.idade_max} anos` : `${g.idade_min || 0}+ anos`;
    const auxNames = g.auxiliares.map(a => a.nome);
    let auxStr = '';
    if (auxNames.length === 1) auxStr = auxNames[0];
    else if (auxNames.length === 2) auxStr = auxNames.join(' e ');
    else if (auxNames.length > 2) auxStr = auxNames.slice(0, -1).join(', ') + ' e ' + auxNames[auxNames.length - 1];
    const header = `${ageLabel}${auxStr ? ' - ' + auxStr : ''}`;
    const list = g.pessoas.map(p => {
      const nb = !p.batizado && p.idade >= 12 ? ' (não batizado)' : '';
      return `- ${p.nome}${nb}`;
    }).join('\n');
    const text = `${header}\n\n${list}`;
    navigator.clipboard.writeText(text);
    setCopied(g.nome + g.genero);
    setTimeout(() => setCopied(null), 2000);
    toast.success('Copiado!');
  };

  if (loading) return <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>;
  if (!data || data.length === 0) return <EmptyState icon={Rows3} text="Nenhuma fila de recitativo configurada" />;

  const totalPessoas = data.reduce((s, g) => s + g.pessoas.length, 0);

  return (
    <div className="space-y-4">
      <Card className="bg-white border-0 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Rows3 className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Filas de Recitativos</h2>
              <p className="text-sm text-slate-500">Agrupamento por faixa etária e auxiliares designados</p>
            </div>
            <Badge className="bg-amber-100 text-amber-700 text-xs font-semibold ml-1">{totalPessoas}</Badge>
          </div>
        </CardHeader>
      </Card>
      {data.map((g, i) => {
        const ageLabel = g.idade_max != null ? `${g.idade_min || 0} à ${g.idade_max} anos` : `${g.idade_min || 0}+ anos`;
        const gKey = g.nome + g.genero;
        return (
          <Card key={i} className="bg-white border-0 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${g.genero === 'feminino' ? 'bg-pink-100' : g.genero === 'masculino' ? 'bg-blue-100' : 'bg-slate-100'}`}>
                    <Rows3 className={`w-5 h-5 ${g.genero === 'feminino' ? 'text-pink-600' : g.genero === 'masculino' ? 'text-blue-600' : 'text-slate-600'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold text-slate-900">{g.nome}</h3>
                      <Badge className={`text-[10px] ${g.genero === 'feminino' ? 'bg-pink-50 text-pink-600' : g.genero === 'masculino' ? 'bg-blue-50 text-blue-600' : 'bg-slate-50 text-slate-600'}`}>{g.genero === 'feminino' ? 'Meninas' : g.genero === 'masculino' ? 'Meninos' : ''}</Badge>
                      <Badge variant="outline" className="text-[10px]">{ageLabel}</Badge>
                    </div>
                    {g.auxiliares.length > 0 && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        {g.auxiliares.map(a => a.nome).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-slate-100 text-slate-700 text-xs">{g.pessoas.length}</Badge>
                  <button onClick={() => copyGroup(g)} className="p-1.5 rounded-md hover:bg-slate-100 transition-colors" title="Copiar lista">
                    {copied === gKey ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-400" />}
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {g.pessoas.length > 0 ? (
                <div>
                  {g.pessoas.map((p, j) => (
                    <div key={p.pessoa_id} className="flex items-center gap-2 px-2 py-1 border-b border-slate-50 last:border-b-0 text-sm">
                      {p.whatsapp ? (
                        <a href={`https://api.whatsapp.com/send/?phone=55${p.whatsapp.replace(/\D/g, '')}&text=&type=phone_number&app_absent=0`} target="_blank" rel="noopener noreferrer" className="text-slate-700 hover:underline">{p.nome}</a>
                      ) : (
                        <span className="text-slate-700">{p.nome}</span>
                      )}
                      {!p.batizado && p.idade >= 12 && <Badge className="bg-red-50 text-red-500 text-[8px] py-0 px-1.5 leading-tight">Não batizado</Badge>}
                      <span className="text-xs text-slate-400 ml-auto flex-shrink-0">{p.idade} anos</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">Nenhuma pessoa nesta faixa etária</p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}


function PessoasEngajadas({ data, onEditPessoa, showEventosBtn, engajamentoDisplay = 'niveis', isAuxiliar = false }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [auxFilter, setAuxFilter] = useState('todas'); // 'todas' | 'auxiliares' | 'nao_auxiliares'

  const openEventos = async (pessoaId) => {
    setModalOpen(true);
    setModalLoading(true);
    setModalData(null);
    try {
      const r = await fetch(`${API}/reports/pessoa/${pessoaId}/eventos`, { credentials: 'include' });
      if (r.ok) setModalData(await r.json());
    } catch (err) { console.error(err); }
    finally { setModalLoading(false); }
  };

  const filtered = auxFilter === 'todas' ? data : auxFilter === 'auxiliares' ? data.filter(p => p.auxiliar_jovens) : data.filter(p => !p.auxiliar_jovens);

  // Sort: by percentage when 'percentual', by level group when 'niveis'
  const sorted = [...filtered].sort((a, b) => {
    if (engajamentoDisplay === 'niveis') {
      const levelOrder = (pct) => pct > 60 ? 0 : pct > 20 ? 1 : 2;
      const diff = levelOrder(a.percentage) - levelOrder(b.percentage);
      if (diff !== 0) return diff;
      return a.pessoa_nome.localeCompare(b.pessoa_nome);
    }
    return b.percentage - a.percentage;
  });

  return (
    <>
    <Card className="bg-white border-0 shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Pessoas Mais Engajadas</h2>
            <p className="text-sm text-slate-500">{engajamentoDisplay === 'niveis' ? 'Agrupado por nível de engajamento' : 'Ranking por quantidade de presenças (decrescente)'}</p>
          </div>
          <Badge className="bg-blue-100 text-blue-700 text-xs font-semibold ml-1">{sorted.length}</Badge>
        </div>
        <div className="flex bg-slate-100 rounded-lg p-0.5 mt-5 w-fit" style={{ marginTop: '16px' }}>
          {[{ key: 'todas', label: 'Todas' }, { key: 'auxiliares', label: 'Auxiliares' }, { key: 'nao_auxiliares', label: 'Não Auxiliares' }].map(f => (
            <button key={f.key} onClick={() => setAuxFilter(f.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${auxFilter === f.key ? 'bg-white shadow-sm text-blue-700' : 'text-slate-600 hover:text-slate-900'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3" data-testid="report-pessoas-list">
          {engajamentoDisplay === 'niveis' ? (() => {
            const groups = { alto: sorted.filter(p => p.percentage > 60), medio: sorted.filter(p => p.percentage > 20 && p.percentage <= 60), baixo: sorted.filter(p => p.percentage <= 20) };
            return [
              { key: 'alto', label: 'Alto Engajamento', items: groups.alto, bg: 'bg-green-50 text-green-700', border: 'border-green-200' },
              { key: 'medio', label: 'Médio Engajamento', items: groups.medio, bg: 'bg-amber-50 text-amber-700', border: 'border-amber-200' },
              { key: 'baixo', label: 'Baixo Engajamento', items: groups.baixo, bg: 'bg-red-50 text-red-700', border: 'border-red-200' },
            ].filter(g => g.items.length > 0).map(g => (
              <div key={g.key}>
                <div className="flex items-center gap-2 mb-2 mt-2">
                  <Badge className={`${g.bg} text-xs font-semibold`}>{g.label}</Badge>
                  <span className="text-xs text-slate-400">{g.items.length}</span>
                </div>
                {g.items.map((item, i) => {
                  return (
                    <div key={item.pessoa_id} className="px-4 py-3 border-b border-slate-100 last:border-b-0">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 ${item.genero === 'feminino' ? 'bg-gradient-to-br from-pink-400 to-pink-500' : 'bg-gradient-to-br from-blue-400 to-blue-500'}`}>{item.pessoa_nome?.charAt(0)}</div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-slate-900 truncate">{item.pessoa_nome}</p>
                          <p className="text-xs text-slate-500 flex items-center gap-1 flex-wrap"><span>{item.attendance_count} presenças em {item.total_events} eventos</span></p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {isAuxiliar ? (<span className={`px-2 py-0.5 rounded text-xs font-semibold ${g.bg}`}>{g.label.split(' ')[0]}</span>) : (<TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><span className={`px-2 py-0.5 rounded text-xs font-semibold ${g.bg}`}>{g.label.split(' ')[0]}</span></TooltipTrigger><TooltipContent><p className="text-xs">{item.percentage}%</p></TooltipContent></Tooltip></TooltipProvider>)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ));
          })() : sorted.map((item, i) => {
            const freqColor = item.percentage >= 70 ? '#10B981' : item.percentage >= 40 ? '#F59E0B' : '#EF4444';
            return (
              <div key={item.pessoa_id} className="px-4 py-3 border-b border-slate-100 last:border-b-0">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`font-semibold text-sm truncate ${onEditPessoa ? 'cursor-pointer hover:underline' : ''}`} style={{ color: '#0f172a' }} onClick={() => onEditPessoa && onEditPessoa(item.pessoa_id)}>{item.pessoa_nome}</p>
                      <Badge variant="outline" className="text-xs">{item.fila_nome || 'Sem Atribuição'}</Badge>
                      <span className="flex items-center gap-1 text-sm text-slate-500"><Calendar className="w-3.5 h-3.5" />{item.attendance_count}/{item.total_events} eventos</span>
                    </div>
                  </div>
                  {showEventosBtn && <button onClick={() => openEventos(item.pessoa_id)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0" data-testid={`btn-ver-eventos-${item.pessoa_id}`} title="Ver eventos">
                    <Eye className="w-4 h-4 text-slate-400" />
                  </button>}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {(() => { const eng = getEngagementDisplay(item.percentage, engajamentoDisplay); return eng.isPercent ? (<><div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(item.percentage, 100)}%`, background: eng.color }}></div></div><span className="text-lg font-semibold w-14 text-right" style={{ color: eng.color }}>{eng.label}</span></>) : isAuxiliar ? (<span className={`px-2 py-0.5 rounded text-xs font-semibold ${eng.bg}`}>{eng.label}</span>) : (<TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><span className={`px-2 py-0.5 rounded text-xs font-semibold ${eng.bg}`}>{eng.label}</span></TooltipTrigger><TooltipContent><p className="text-xs">{item.percentage}%</p></TooltipContent></Tooltip></TooltipProvider>); })()}
                  </div>
                </div>
              </div>
            );
          })}
          {data.length === 0 && <EmptyState icon={Users} text="Sem dados de frequência" />}
        </div>
      </CardContent>
    </Card>
    <EventosModal open={modalOpen} onOpenChange={setModalOpen} data={modalData} loading={modalLoading} />
    </>
  );
}

function AuxiliaresEngajados({ pessoas, onEditPessoa, showEventosBtn }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const openEventos = async (pessoaId) => {
    setModalOpen(true);
    setModalLoading(true);
    setModalData(null);
    try {
      const r = await fetch(`${API}/reports/pessoa/${pessoaId}/eventos`, { credentials: 'include' });
      if (r.ok) setModalData(await r.json());
    } catch (err) { console.error(err); }
    finally { setModalLoading(false); }
  };

  const auxiliares = pessoas
    .filter(p => p.auxiliar_jovens)
    .sort((a, b) => (b.frequencia_pct || 0) - (a.frequencia_pct || 0));

  return (
    <>
    <Card className="bg-white border-0 shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
            <HandHelping className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Auxiliares de Jovens e Menores</h2>
            <p className="text-sm text-slate-500">Ranking por frequência de presença</p>
          </div>
          <Badge className="bg-green-100 text-green-700 text-xs font-semibold ml-1">{auxiliares.length}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3" data-testid="report-auxiliares-list">
          {auxiliares.map((p, i) => {
            const freq = p.frequencia_pct || 0;
            const freqColor = freq >= 70 ? '#10B981' : freq >= 40 ? '#F59E0B' : '#EF4444';
            return (
              <div key={p.pessoa_id} className="px-4 py-3 border-b border-slate-100 last:border-b-0">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`font-semibold text-sm truncate ${onEditPessoa ? 'cursor-pointer hover:underline' : ''}`} style={{ color: '#0f172a' }} onClick={() => onEditPessoa && onEditPessoa(p.pessoa_id)}>{p.nome_completo}</p>
                      {p.fila && <Badge variant="outline" className="text-xs">{p.fila}</Badge>}
                      <span className="flex items-center gap-1 text-sm text-slate-500"><Calendar className="w-3.5 h-3.5" />{p.eventos_participou}/{p.total_eventos} eventos</span>
                    </div>
                  </div>
                  {showEventosBtn && <button onClick={() => openEventos(p.pessoa_id)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0" data-testid={`btn-ver-eventos-aux-${p.pessoa_id}`} title="Ver eventos">
                    <Eye className="w-4 h-4 text-slate-400" />
                  </button>}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${Math.min(freq, 100)}%`, background: freqColor }}></div>
                    </div>
                    <span className="text-lg font-semibold w-14 text-right" style={{ color: freqColor }}>{freq}%</span>
                  </div>
                </div>
              </div>
            );
          })}
          {auxiliares.length === 0 && <EmptyState icon={HandHelping} text="Nenhuma pessoa marcada como auxiliar de jovens" />}
        </div>
      </CardContent>
    </Card>
    <EventosModal open={modalOpen} onOpenChange={setModalOpen} data={modalData} loading={modalLoading} />
    </>
  );
}

function EventosEngajados({ data }) {
  return (
    <Card className="bg-white border-0 shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
            <Calendar className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Eventos Mais Engajados</h2>
            <p className="text-sm text-slate-500">Ranking por participação</p>
          </div>
          <Badge className="bg-purple-100 text-purple-700 text-xs font-semibold ml-1">{data.length}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3" data-testid="report-eventos-list">
          {data.map((ev, i) => {
            const freq = ev.frequencia_pct ?? 0;
            const freqColor = freq >= 70 ? '#10B981' : freq >= 40 ? '#F59E0B' : '#EF4444';
            return (
              <div key={ev.event_id} className="px-4 py-3 border-b border-slate-100 last:border-b-0">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm text-slate-900 truncate">{ev.name}</p>
                      <Badge className={`text-xs ${ev.status === 'aberto' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>{ev.status === 'aberto' ? 'Aberto' : 'Fechado'}</Badge>
                      <span className="flex items-center gap-1 text-sm text-slate-500"><Calendar className="w-3.5 h-3.5" />{fmtDate(ev.date)}</span>
                      <span className="flex items-center gap-1 text-sm text-slate-500"><Users className="w-3.5 h-3.5" />{ev.presentes}/{ev.total_pessoas} presentes</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${Math.min(freq, 100)}%`, background: freqColor }}></div>
                    </div>
                    <span className="text-lg font-semibold w-14 text-right" style={{ color: freqColor }}>{freq}%</span>
                  </div>
                </div>
              </div>
            );
          })}
          {data.length === 0 && <EmptyState icon={Calendar} text="Sem dados de eventos" />}
        </div>
      </CardContent>
    </Card>
  );
}

function FilasEngajamento({ data, pessoas }) {
  const filaCorMap = {};
  for (const p of pessoas) {
    if (p.fila && p.fila_cor) filaCorMap[p.fila] = p.fila_cor;
  }

  const GENEROS = [
    { key: 'masculino', label: 'Masculino', color: '#3B82F6', bg: 'bg-blue-50', border: 'border-blue-200' },
    { key: 'feminino', label: 'Feminino', color: '#EC4899', bg: 'bg-pink-50', border: 'border-pink-200' },
  ];

  // Group by genero → fila
  const grouped = {};
  for (const item of data) {
    const genero = item.genero || 'outro';
    const fila = item.fila_nome || 'Sem Atribuição';
    if (!grouped[genero]) grouped[genero] = {};
    if (!grouped[genero][fila]) grouped[genero][fila] = { totalPct: 0, count: 0, totalPresencas: 0, totalEventos: item.total_events || 0 };
    grouped[genero][fila].totalPct += (item.percentage || 0);
    grouped[genero][fila].count += 1;
    grouped[genero][fila].totalPresencas += (item.attendance_count || 0);
  }

  return (
    <Card className="bg-white border-0 shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Rows3 className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Filas Mais Engajadas</h2>
            <p className="text-sm text-slate-500">Ranking por engajamento médio, agrupado por género</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6" data-testid="report-filas-engajamento-list">
          {GENEROS.map((g) => {
            const filaData = grouped[g.key] || {};
            const ranked = Object.entries(filaData)
              .map(([fila, stats]) => ({
                fila,
                cor: filaCorMap[fila] || '#94A3B8',
                count: stats.count,
                avgPct: Math.round(stats.totalPct / stats.count),
                totalPresencas: stats.totalPresencas,
                totalEventos: stats.totalEventos,
              }))
              .sort((a, b) => b.avgPct - a.avgPct);

            return (
              <div key={g.key}>
                <div className={`flex items-center gap-2 mb-3 px-3 py-2 rounded-lg ${g.bg} ${g.border} border`}>
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: g.color }}></div>
                  <p className="font-semibold text-sm" style={{ color: g.color }}>{g.label}</p>
                  <span className="text-xs text-slate-500 ml-auto">{ranked.length} filas</span>
                </div>
                <div className="space-y-2">
                  {ranked.map((item) => {
                    const freqColor = item.avgPct >= 70 ? '#10B981' : item.avgPct >= 40 ? '#F59E0B' : '#EF4444';
                    return (
                      <div key={item.fila} className="px-4 py-3 border-b border-slate-100 last:border-b-0">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0" style={{ backgroundColor: item.cor }}>{item.fila.charAt(0)}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold text-sm text-slate-900">{item.fila}</p>
                              <span className="text-xs text-slate-500">{item.count} membros</span>
                              <span className="flex items-center gap-1 text-xs text-slate-400"><Calendar className="w-3 h-3" />{item.totalPresencas} presenças em {item.totalEventos} ev.</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${Math.min(item.avgPct, 100)}%`, background: freqColor }}></div>
                            </div>
                            <span className="text-lg font-semibold w-14 text-right" style={{ color: freqColor }}>{item.avgPct}%</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {ranked.length === 0 && <p className="text-sm text-slate-400 px-4 py-2">Sem dados</p>}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}


function RelatorioFilas({ pessoas, onEditPessoa, engajamentoDisplay = 'niveis', isAuxiliar = false }) {
  // Group by dynamic fila field from pessoasStats
  const filaGroupMap = {};
  for (const p of pessoas) {
    const filaName = p.fila || 'Sem Atribuição';
    const filaCor = p.fila_cor || '#94A3B8';
    if (!filaGroupMap[filaName]) {
      filaGroupMap[filaName] = { label: filaName, cor: filaCor, members: [] };
    }
    filaGroupMap[filaName].members.push(p);
  }
  const grouped = Object.values(filaGroupMap);
  const total = pessoas.length || 1;

  return (
    <Card className="bg-white border-0 shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Rows3 className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Filas</h2>
            <p className="text-sm text-slate-500">Distribuição por faixa etária</p>
          </div>
          <Badge className="bg-amber-100 text-amber-700 text-xs font-semibold ml-1">{pessoas.length} pessoas</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {/* Grafico de barras */}
        <div className="grid gap-3 mb-8 overflow-x-auto" style={{ gridTemplateColumns: `repeat(${Math.min(grouped.length, 6)}, minmax(80px, 1fr))` }} data-testid="filas-chart">
          {grouped.map((fila) => {
            const pct = Math.round((fila.members.length / total) * 100);
            return (
              <div key={fila.label} className="text-center">
                <div className="h-32 flex items-end justify-center mb-2">
                  <div className="w-12 rounded-t-lg transition-all" style={{ height: `${Math.max(pct, 8)}%`, background: fila.cor }}></div>
                </div>
                <p className="text-xs font-semibold text-slate-700">{fila.label}</p>
                <p className="text-lg font-bold text-slate-900">{fila.members.length}</p>
                <p className="text-xs text-slate-500">{pct}%</p>
              </div>
            );
          })}
        </div>

        {/* Lista por fila */}
        <div className="flex flex-col gap-5" data-testid="report-filas">
          {grouped.map((fila) => (
            <div key={fila.label} className="bg-slate-50/50 rounded-2xl border border-slate-100 p-5">
              <div className="flex items-center gap-3 mb-4">
                <Badge className="text-xs font-semibold" style={{ backgroundColor: fila.cor, color: getFilaTextColor(fila.cor) }}>{fila.label}</Badge>
                <Badge variant="outline" className="text-xs">{fila.members.length} {fila.members.length === 1 ? 'pessoa' : 'pessoas'}</Badge>
              </div>
              {fila.members.length > 0 ? (
                <div className="flex flex-col gap-0">
                  {fila.members.map((p) => (
                    <div key={p.pessoa_id} className="px-4 py-3 bg-white border-b border-slate-100 last:border-b-0">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0" style={{ backgroundColor: fila.cor || '#94A3B8' }}>{p.nome_completo.charAt(0)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`font-semibold text-sm truncate ${onEditPessoa ? 'cursor-pointer hover:underline' : ''}`} style={{ color: '#0f172a' }} onClick={() => onEditPessoa && onEditPessoa(p.pessoa_id)}>{p.nome_completo}</p>
                            <Badge variant="outline" className="text-xs">{calcAge(p.data_nascimento)}</Badge>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {(() => { const pct = p.frequencia_pct || 0; const eng = getEngagementDisplay(pct, engajamentoDisplay); return eng.isPercent ? (<><div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: eng.color }}></div></div><span className="text-sm font-semibold w-12 text-right" style={{ color: eng.color }}>{eng.label}</span></>) : isAuxiliar ? (<span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${eng.bg}`}>{eng.label}</span>) : (<TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${eng.bg}`}>{eng.label}</span></TooltipTrigger><TooltipContent><p className="text-xs">{pct}%</p></TooltipContent></Tooltip></TooltipProvider>); })()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-2">Nenhuma pessoa nesta fila</p>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

const GENEROS = [
  { label: 'Masculino', value: 'masculino', color: 'bg-blue-100 text-blue-700 border-blue-200', barColor: '#3B82F6' },
  { label: 'Feminino', value: 'feminino', color: 'bg-pink-100 text-pink-700 border-pink-200', barColor: '#EC4899' },
];

function RelatorioGenero({ pessoas, onEditPessoa, engajamentoDisplay = 'niveis', isAuxiliar = false }) {
  const grouped = GENEROS.map(g => ({
    ...g,
    members: pessoas.filter(p => p.genero === g.value),
  }));
  const total = pessoas.length || 1;

  return (
    <Card className="bg-white border-0 shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-rose-100 flex items-center justify-center flex-shrink-0">
            <UserCircle className="w-5 h-5 text-rose-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Gênero</h2>
            <p className="text-sm text-slate-500">Distribuição por gênero</p>
          </div>
          <Badge className="bg-rose-100 text-rose-700 text-xs font-semibold ml-1">{pessoas.length} pessoas</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {/* Gráfico de barras */}
        <div className="grid grid-cols-2 gap-3 mb-8" data-testid="genero-chart">
          {grouped.map((g) => {
            const pct = Math.round((g.members.length / total) * 100);
            return (
              <div key={g.value} className="text-center">
                <div className="h-32 flex items-end justify-center mb-2">
                  <div className="w-16 rounded-t-lg transition-all" style={{ height: `${Math.max(pct, 8)}%`, background: g.barColor }}></div>
                </div>
                <p className="text-xs font-semibold text-slate-700">{g.label}</p>
                <p className="text-lg font-bold text-slate-900">{g.members.length}</p>
                <p className="text-xs text-slate-500">{pct}%</p>
              </div>
            );
          })}
        </div>

        {/* Lista por gênero */}
        <div className="flex flex-col gap-5" data-testid="report-genero">
          {grouped.map((g) => (
            <div key={g.value} className="bg-slate-50/50 rounded-2xl border border-slate-100 p-5">
              <div className="flex items-center gap-3 mb-4">
                <Badge className={`text-xs font-semibold ${g.color}`}>{g.label}</Badge>
                <Badge variant="outline" className="text-xs">{g.members.length} {g.members.length === 1 ? 'pessoa' : 'pessoas'}</Badge>
              </div>
              {g.members.length > 0 ? (
                <div className="flex flex-col gap-0">
                  {g.members.map((p) => (
                    <div key={p.pessoa_id} className="px-4 py-3 bg-white border-b border-slate-100 last:border-b-0">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0" style={{ backgroundColor: g.barColor }}>{p.nome_completo.charAt(0)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`font-semibold text-sm truncate ${onEditPessoa ? 'cursor-pointer hover:underline' : ''}`} style={{ color: '#0f172a' }} onClick={() => onEditPessoa && onEditPessoa(p.pessoa_id)}>{p.nome_completo}</p>
                            <Badge variant="outline" className="text-xs">{calcAge(p.data_nascimento)}</Badge>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {(() => { const pct = p.frequencia_pct || 0; const eng = getEngagementDisplay(pct, engajamentoDisplay); return eng.isPercent ? (<><div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: eng.color }}></div></div><span className="text-sm font-semibold w-12 text-right" style={{ color: eng.color }}>{eng.label}</span></>) : isAuxiliar ? (<span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${eng.bg}`}>{eng.label}</span>) : (<TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${eng.bg}`}>{eng.label}</span></TooltipTrigger><TooltipContent><p className="text-xs">{pct}%</p></TooltipContent></Tooltip></TooltipProvider>); })()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-2">Nenhuma pessoa</p>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function RelatorioTipoEvento({ data }) {
  const events = data.events || [];
  const closedEvents = events.filter(e => e.status === 'fechado');
  const openEvents = events.filter(e => e.status === 'aberto');
  const hasParticipantes = events.some(e => e.qtd_participantes > 0);
  const [chartMode, setChartMode] = useState('presencas'); // 'presencas' | 'participantes'
  const getValue = (ev) => chartMode === 'participantes' ? (ev.qtd_participantes || 0) : ev.presentes;
  const maxVal = Math.max(...events.map(e => getValue(e)), 1);

  // Color palette for bars
  const BAR_COLORS = ['#6366F1', '#8B5CF6', '#A78BFA', '#7C3AED', '#818CF8', '#4F46E5'];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="tipo-evento-summary">
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <p className="text-[10px] sm:text-xs text-slate-500 mb-1 sm:mb-2">Total de Eventos</p>
            <div className="flex items-center gap-2">
              <Hash className="w-5 h-5 text-indigo-600" />
              <p className="text-xl sm:text-2xl font-bold text-indigo-600">{data.total_events}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <p className="text-[10px] sm:text-xs text-slate-500 mb-1 sm:mb-2">Participação Média</p>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <p className="text-xl sm:text-2xl font-bold text-green-600">{data.avg_attendance_pct}%</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <p className="text-[10px] sm:text-xs text-slate-500 mb-1 sm:mb-2">Total Presenças</p>
            <div className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-blue-600" />
              <p className="text-xl sm:text-2xl font-bold text-blue-600">{data.total_presentes}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <p className="text-[10px] sm:text-xs text-slate-500 mb-1 sm:mb-2">Fechados / Abertos</p>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-500" />
              <p className="text-xl sm:text-2xl font-bold text-orange-500">{closedEvents.length}<span className="text-slate-400 text-base font-normal">/{openEvents.length}</span></p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bar Chart - Attendance per Event */}
      <Card className="bg-white border-0 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <Tag className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{data.tipo_nome}</h2>
              <p className="text-sm text-slate-500">Participação por evento</p>
            </div>
            <Badge className="bg-indigo-100 text-indigo-700 text-xs font-semibold ml-1">{data.total_events} eventos</Badge>
          </div>
          <div className="flex bg-slate-100 rounded-lg p-0.5 w-fit" style={{ marginTop: '16px' }}>
            <button onClick={() => setChartMode('presencas')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${chartMode === 'presencas' ? 'bg-white shadow-sm text-indigo-700' : 'text-slate-600 hover:text-slate-900'}`}>
              Presenças
            </button>
            <button onClick={() => hasParticipantes && setChartMode('participantes')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${chartMode === 'participantes' ? 'bg-white shadow-sm text-indigo-700' : hasParticipantes ? 'text-slate-600 hover:text-slate-900' : 'text-slate-300 cursor-not-allowed'}`}
              title={!hasParticipantes ? 'Nenhum evento com participantes registrados' : ''}>
              Participantes
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {events.length > 0 ? (
            <>
              {/* Chart */}
              <div className="overflow-x-auto mb-8">
                <div className="flex items-end gap-2 sm:gap-3 min-w-0" style={{ minWidth: events.length > 6 ? `${events.length * 80}px` : 'auto' }} data-testid="tipo-evento-chart">
                  {[...events].reverse().map((ev, i) => {
                    const val = getValue(ev);
                    const pct = ev.total_pessoas > 0 ? Math.round((ev.presentes / ev.total_pessoas) * 100) : 0;
                    const barHeight = maxVal > 0 ? Math.max((val / maxVal) * 100, 8) : 8;
                    const barColor = BAR_COLORS[i % BAR_COLORS.length];
                    return (
                      <div key={ev.event_id} className="flex-1 min-w-[60px] max-w-[100px] text-center">
                        <div className="h-36 flex items-end justify-center mb-2">
                          <div className="w-10 sm:w-12 rounded-t-lg transition-all relative group cursor-default" style={{ height: `${barHeight}%`, background: barColor }}>
                            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[10px] px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                              {chartMode === 'participantes' ? `${val} participantes` : `${ev.presentes}/${ev.total_pessoas} (${pct}%)`}
                            </div>
                          </div>
                        </div>
                        <p className="text-[10px] sm:text-xs font-medium text-slate-600 truncate">{ev.name}</p>
                        <p className="text-xs text-slate-400">{fmtDate(ev.date)}</p>
                        <p className="text-sm font-bold text-slate-900">{val}</p>
                        {chartMode === 'presencas' && <p className="text-[10px] text-slate-500">{pct}%</p>}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Events List */}
              <div className="flex flex-col gap-0" data-testid="tipo-evento-events-list">
                {events.map((ev) => {
                  const pct = ev.frequencia_pct || 0;
                  const freqColor = pct >= 70 ? '#10B981' : pct >= 40 ? '#F59E0B' : '#EF4444';
                  return (
                    <div key={ev.event_id} className="px-4 py-3 border-b border-slate-100 last:border-b-0">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                          <Calendar className="w-3.5 h-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-sm text-slate-900 truncate">{ev.name}</p>
                            <Badge className={`text-xs ${ev.status === 'aberto' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>
                              {ev.status === 'aberto' ? 'Aberto' : 'Fechado'}
                            </Badge>
                            <span className="text-xs text-slate-500">{fmtDate(ev.date)}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 flex-wrap">
                            <span className="flex items-center gap-1 text-xs text-slate-500"><Users className="w-3 h-3" />{ev.presentes}/{ev.total_pessoas}</span>
                            {ev.quem_presidiu && <span className="text-xs text-slate-400">Presidiu: {ev.quem_presidiu}</span>}
                            {ev.qtd_musicos != null && <span className="text-xs text-slate-400">Músicos: {ev.qtd_musicos}</span>}
                            {ev.qtd_ministerio != null && <span className="text-xs text-slate-400">Ministério: {ev.qtd_ministerio}</span>}
                            {ev.qtd_participantes != null && <span className="text-xs text-slate-400">Participantes: {ev.qtd_participantes}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: freqColor }}></div>
                          </div>
                          <span className="text-sm font-semibold w-12 text-right" style={{ color: freqColor }}>{pct}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <EmptyState icon={Tag} text="Nenhum evento deste tipo encontrado" />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RelatorioNaoBatizados({ pessoas, onEditPessoa }) {
  const getAge = (dob) => {
    if (!dob) return null;
    const [y, m, d] = dob.split('-').map(Number);
    const today = new Date();
    let age = today.getFullYear() - y;
    if (today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)) age--;
    return age;
  };

  const naoBatizados = pessoas.filter(p => {
    const age = getAge(p.data_nascimento);
    return age !== null && age >= 12 && !p.batizado;
  }).sort((a, b) => (getAge(b.data_nascimento) || 0) - (getAge(a.data_nascimento) || 0));

  return (
    <Card className="bg-white border-0 shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
            <UserCheck className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Não Batizados</h2>
            <p className="text-sm text-slate-500">Pessoas com 12 anos ou mais que não são batizadas</p>
          </div>
          <Badge className="bg-red-100 text-red-700 text-xs font-semibold ml-1">{naoBatizados.length} pessoas</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {naoBatizados.length > 0 ? (
          <div className="flex flex-col gap-0" data-testid="report-nao-batizados">
            {naoBatizados.map((p) => {
              const age = getAge(p.data_nascimento);
              const freqPct = p.frequencia_pct || 0;
              const freqColor = freqPct >= 70 ? '#10B981' : freqPct >= 40 ? '#F59E0B' : '#EF4444';
              return (
                <div key={p.pessoa_id} className="px-4 py-3 border-b border-slate-100 last:border-b-0" data-testid={`nao-batizado-${p.pessoa_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold text-xs flex-shrink-0">{p.nome_completo.charAt(0)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className={`font-semibold text-sm truncate ${onEditPessoa ? 'cursor-pointer hover:underline' : ''}`} style={{ color: '#0f172a' }} onClick={() => onEditPessoa && onEditPessoa(p.pessoa_id)}>{p.nome_completo}</p>
                        <Badge variant="outline" className="text-xs">{age} anos</Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${Math.min(freqPct, 100)}%`, background: freqColor }}></div>
                      </div>
                      <span className="text-sm font-semibold w-12 text-right" style={{ color: freqColor }}>{freqPct}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState icon={UserCheck} text="Todas as pessoas com 12+ anos são batizadas" />
        )}
      </CardContent>
    </Card>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="text-center py-12">
      <Icon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
      <p className="text-slate-500 text-sm">{text}</p>
    </div>
  );
}

function MigracoesHistorico({ onEditPessoa }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMes, setFilterMes] = useState('all');

  useEffect(() => {
    fetchData();
  }, [filterMes]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterMes !== 'all') params.append('mes', filterMes);
      const r = await fetch(`${API}/migracoes/historico?${params}`, { credentials: 'include' });
      if (r.ok) setData(await r.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // Generate month options from data
  const mesesDisponiveis = [...new Set(data.map(m => m.data_migracao?.substring(0, 7)).filter(Boolean))].sort().reverse();

  // Group by month
  const grouped = {};
  for (const m of data) {
    const mes = m.data_migracao?.substring(0, 7) || 'N/A';
    if (!grouped[mes]) grouped[mes] = [];
    grouped[mes].push(m);
  }

  const formatMonth = (ym) => {
    if (!ym || ym === 'N/A') return 'N/A';
    const [y, m] = ym.split('-');
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${months[parseInt(m) - 1]} ${y}`;
  };

  return (
    <Card className="bg-white border-0 shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
            <ArrowRight className="w-5 h-5 text-teal-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Histórico de Migrações</h2>
            <p className="text-sm text-slate-500">Mudanças de atribuição registradas</p>
          </div>
          <Badge className="bg-teal-100 text-teal-700 text-xs font-semibold">{data.length} registros</Badge>
        </div>
        <div className="flex gap-3 mt-4 flex-wrap">
          <Select value={filterMes} onValueChange={setFilterMes}>
            <SelectTrigger className="w-44 h-9 text-sm" data-testid="filter-migracoes-mes">
              <SelectValue placeholder="Mês" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os meses</SelectItem>
              {mesesDisponiveis.map(m => (
                <SelectItem key={m} value={m}>{formatMonth(m)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-3 text-sm text-slate-500">Carregando...</p>
          </div>
        ) : data.length === 0 ? (
          <EmptyState icon={ArrowRight} text="Nenhuma migração registrada" />
        ) : (
          <div className="space-y-6" data-testid="migracoes-historico-list">
            {Object.entries(grouped).sort(([a], [b]) => b.localeCompare(a)).map(([mes, items]) => (
              <div key={mes}>
                <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
                  <Calendar className="w-4 h-4 text-slate-500" />
                  <p className="font-semibold text-sm text-slate-700">{formatMonth(mes)}</p>
                  <Badge variant="outline" className="text-xs ml-auto">{items.length} {items.length === 1 ? 'migração' : 'migrações'}</Badge>
                </div>
                <div className="space-y-0">
                  {items.map((m) => (
                    <div key={m.migracao_id} className="px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors" data-testid={`migracao-${m.migracao_id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0" style={{ backgroundColor: m.fila_nova_cor || '#94A3B8' }}>{(m.nome_completo || '?').charAt(0)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`font-semibold text-sm truncate ${onEditPessoa ? 'cursor-pointer hover:underline' : ''}`} style={{ color: '#0f172a' }} onClick={() => onEditPessoa && onEditPessoa(m.pessoa_id)}>{m.nome_completo}</p>
                            <span className="text-xs text-slate-400">{fmtDate(m.data_migracao)}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <Badge className="text-xs" style={{ backgroundColor: m.fila_anterior_cor || '#94A3B8', color: getFilaTextColor(m.fila_anterior_cor || '#94A3B8') }}>{m.fila_anterior_nome}</Badge>
                            <ArrowRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                            <Badge className="text-xs" style={{ backgroundColor: m.fila_nova_cor || '#94A3B8', color: getFilaTextColor(m.fila_nova_cor || '#94A3B8') }}>{m.fila_nova_nome}</Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MigracoesFuturas({ onEditPessoa }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMeses, setFilterMeses] = useState('6');

  useEffect(() => {
    fetchData();
  }, [filterMeses]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ meses: filterMeses });
      const r = await fetch(`${API}/migracoes/previsao?${params}`, { credentials: 'include' });
      if (r.ok) setData(await r.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const formatMonth = (ym) => {
    if (!ym) return 'N/A';
    const [y, m] = ym.split('-');
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${months[parseInt(m) - 1]} ${y}`;
  };

  // Group by month
  const grouped = {};
  for (const m of data) {
    const mes = m.data_aniversario?.substring(0, 7) || 'N/A';
    if (!grouped[mes]) grouped[mes] = [];
    grouped[mes].push(m);
  }

  // Summary per fila: gains and losses
  const filaSummary = {};
  for (const m of data) {
    if (m.fila_atual_id) {
      if (!filaSummary[m.fila_atual_id]) filaSummary[m.fila_atual_id] = { nome: m.fila_atual_nome, cor: m.fila_atual_cor, saem: 0, entram: 0 };
      filaSummary[m.fila_atual_id].saem++;
    }
    if (m.fila_futura_id) {
      if (!filaSummary[m.fila_futura_id]) filaSummary[m.fila_futura_id] = { nome: m.fila_futura_nome, cor: m.fila_futura_cor, saem: 0, entram: 0 };
      filaSummary[m.fila_futura_id].entram++;
    }
  }

  return (
    <Card className="bg-white border-0 shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <CalendarClock className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Migrações Futuras</h2>
            <p className="text-sm text-slate-500">Previsão de mudanças de atribuição</p>
          </div>
          <Badge className="bg-emerald-100 text-emerald-700 text-xs font-semibold">{data.length} previsões</Badge>
        </div>
        <div className="flex gap-3 mt-4 flex-wrap">
          <Select value={filterMeses} onValueChange={setFilterMeses}>
            <SelectTrigger className="w-44 h-9 text-sm" data-testid="filter-previsao-meses">
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Próximo 1 mês</SelectItem>
              <SelectItem value="3">Próximos 3 meses</SelectItem>
              <SelectItem value="6">Próximos 6 meses</SelectItem>
              <SelectItem value="12">Próximos 12 meses</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
            <p className="mt-3 text-sm text-slate-500">Carregando...</p>
          </div>
        ) : data.length === 0 ? (
          <EmptyState icon={CalendarClock} text="Nenhuma migração prevista para o período selecionado" />
        ) : (
          <div className="space-y-6" data-testid="migracoes-futuras-list">
            {/* Summary cards */}
            {Object.keys(filaSummary).length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4" data-testid="migracoes-summary">
                {Object.entries(filaSummary).map(([filaId, s]) => (
                  <div key={filaId} className="rounded-lg border border-slate-200 p-3 bg-slate-50/50">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.cor || '#94A3B8' }}></div>
                      <p className="text-xs font-semibold text-slate-700 truncate">{s.nome}</p>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      {s.entram > 0 && <span className="text-green-600 font-semibold">+{s.entram} entram</span>}
                      {s.saem > 0 && <span className="text-red-500 font-semibold">-{s.saem} saem</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Detail by month */}
            {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([mes, items]) => (
              <div key={mes}>
                <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
                  <CalendarClock className="w-4 h-4 text-slate-400" />
                  <p className="font-semibold text-sm text-slate-500">{formatMonth(mes)}</p>
                  <Badge variant="outline" className="text-xs ml-auto border-slate-300 text-slate-500">{items.length} {items.length === 1 ? 'migração' : 'migrações'}</Badge>
                </div>
                <div className="space-y-0">
                  {items.map((m) => (
                    <div key={m.pessoa_id} className="px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors" data-testid={`previsao-${m.pessoa_id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0" style={{ backgroundColor: m.fila_futura_cor || '#94A3B8' }}>{(m.nome_completo || '?').charAt(0)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`font-semibold text-sm truncate ${onEditPessoa ? 'cursor-pointer hover:underline' : ''}`} style={{ color: '#0f172a' }} onClick={() => onEditPessoa && onEditPessoa(m.pessoa_id)}>{m.nome_completo}</p>
                            <span className="text-xs text-slate-400">Aniversário: {fmtDate(m.data_aniversario)}</span>
                            <Badge variant="outline" className="text-xs">{m.idade_completa} anos</Badge>
                          </div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <Badge className="text-xs" style={{ backgroundColor: m.fila_atual_cor || '#94A3B8', color: getFilaTextColor(m.fila_atual_cor || '#94A3B8') }}>{m.fila_atual_nome}</Badge>
                            <ArrowRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                            <Badge className="text-xs" style={{ backgroundColor: m.fila_futura_cor || '#94A3B8', color: getFilaTextColor(m.fila_futura_cor || '#94A3B8') }}>{m.fila_futura_nome}</Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
